import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useCaseInvoices } from '../../../hooks/useCaseInvoices';
import jsPDF from 'jspdf';

// ─────────────────────────────────────────────────────────────
// Status config
// ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  paid:            { cls: 'bg-success/15 text-success border-success/20',           icon: 'CheckCircle',  label: 'Payée' },
  pending:         { cls: 'bg-warning/15 text-warning border-warning/20',            icon: 'Clock',        label: 'En attente' },
  overdue:         { cls: 'bg-error/15 text-error border-error/20',                  icon: 'AlertCircle',  label: 'En retard' },
  partial:         { cls: 'bg-blue-500/15 text-blue-600 border-blue-500/20',         icon: 'PieChart',     label: 'Partiel' },
  partially_paid:  { cls: 'bg-blue-500/15 text-blue-600 border-blue-500/20',         icon: 'PieChart',     label: 'Part. payée' },
  draft:           { cls: 'bg-muted text-muted-foreground border-border',            icon: 'FileText',     label: 'Brouillon' },
  cancelled:       { cls: 'bg-muted text-muted-foreground border-border',            icon: 'XCircle',      label: 'Annulée' },
  in_collection:   { cls: 'bg-orange-500/15 text-orange-700 border-orange-500/20',  icon: 'AlertTriangle',label: 'Recouvrement' },
  waived:          { cls: 'bg-muted text-muted-foreground border-border',            icon: 'Slash',        label: 'Exonérée' },
};

const getStatus = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.pending;

// ─────────────────────────────────────────────────────────────
// PDF generation
// ─────────────────────────────────────────────────────────────
const downloadInvoicePdf = (inv) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const currencyCode = inv.currencyCode || 'EUR';
  const W = 210;
  const margin = 14;
  const contentW = W - margin * 2;

  const fmt = (val) => {
    const formatted = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
    }).format(val ?? 0);
    return formatted
      .replace(/\u202f/g, ' ')
      .replace(/\u00a0/g, ' ');
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  // Colors
  const indigo   = [79, 70, 229];
  const gray100  = [243, 244, 246];
  const gray400  = [156, 163, 175];
  const gray700  = [55, 65, 81];
  const green    = [22, 163, 74];
  const red      = [220, 38, 38];
  const white    = [255, 255, 255];

  let y = margin;

  const rect = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, 'F');
  };

  const text = (str, x, ty, opts = {}) => {
    doc.setFontSize(opts.size || 10);
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setTextColor(...(opts.color || gray700));
    doc.text(String(str ?? '—'), x, ty, { align: opts.align || 'left' });
  };

  const line = (ly, color = gray100) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(margin, ly, W - margin, ly);
  };

  // ── Header band ──
  rect(0, 0, W, 32, indigo);

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('ClearMotion', margin, 14);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text('Laboratoire dentaire orthodontique', margin, 20);

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('FACTURE', W - margin, 13, { align: 'right' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text(inv.number, W - margin, 20, { align: 'right' });

  const statusLabel = inv.status === 'paid' ? 'PAYÉE' : 'ENVOYÉE';
  const statusColor = inv.status === 'paid' ? [220, 252, 231] : [254, 249, 195];
  const statusText  = inv.status === 'paid' ? [22, 101, 52]   : [133, 77, 14];
  doc.setFillColor(...statusColor);
  doc.roundedRect(W - margin - 28, 23, 28, 6.5, 2, 2, 'F');
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...statusText);
  doc.text(statusLabel, W - margin - 14, 27.5, { align: 'center' });

  y = 40;

  // ── Dates row ──
  rect(margin, y, contentW, 14, gray100);
  const col = contentW / 3;
  const dateFields = [
    { label: "Date d'émission",  val: fmtDate(inv.rawIssueDate   || inv.issueDate) },
    { label: "Date d'échéance",  val: fmtDate(inv.rawDueDate     || inv.dueDate) },
    { label: 'Date de paiement', val: fmtDate(inv.rawPaymentDate || inv.paymentDate) },
  ];
  dateFields.forEach((f, i) => {
    const cx = margin + i * col + col / 2;
    text(f.label, cx, y + 5,  { size: 7.5, color: gray400, align: 'center' });
    text(f.val,   cx, y + 11, { size: 8.5, bold: true, color: gray700, align: 'center' });
  });

  y += 20;

  // ── Parties ──
  const halfW = (contentW - 6) / 2;

  rect(margin, y, halfW, 26, gray100);
  text('FACTURÉ À',          margin + 4, y + 6,  { size: 7.5, color: gray400, bold: true });
  text(inv.dentistName || inv.patientName || '—', margin + 4, y + 13, { size: 9, bold: true });
  text(inv.dentistEmail || '—',                   margin + 4, y + 19, { size: 8, color: gray400 });

  const cx2 = margin + halfW + 6;
  rect(cx2, y, halfW, 26, gray100);
  text('INFORMATIONS DU CAS', cx2 + 4, y + 6,  { size: 7.5, color: gray400, bold: true });
  text(`Cas : ${inv.caseNumber || '—'}`,          cx2 + 4, y + 13, { size: 9, bold: true });
  text(`Patient : ${inv.patientName || '—'}`,     cx2 + 4, y + 19, { size: 8, color: gray400 });


  y += 33;

  // ── Table header ──
  rect(margin, y, contentW, 9, indigo);
  const cols = [
    { label: 'DESCRIPTION', x: margin + 3,        align: 'left'   },
    { label: 'ALIGNEURS',   x: margin + 85 + 12.5, align: 'center' },
    { label: 'PLAGE',       x: margin + 112 + 15,  align: 'center' },
    { label: 'MONTANT HT',  x: W - margin - 3,     align: 'right'  },
  ];
  cols.forEach((c) => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...white);
    doc.text(c.label, c.x, y + 6, { align: c.align });
  });
  y += 9;

  // ── Table row ──
  rect(margin, y, contentW, 18, [249, 250, 251]);
  line(y, [229, 231, 235]);

  text('LOT '+inv.phaseNumber || 'Phase de traitement', margin + 3, y + 7,  { size: 9, bold: true });
  text(`Cas ${inv.caseNumber || '—'} — ${inv.patientName || ''}`, margin + 3, y + 13, { size: 7.5, color: gray400 });

  text(String(inv.alignersCount ?? '—'), margin + 85 + 12.5, y + 10, { size: 9, align: 'center' });

  const rangeLabel = inv.alignerRangeFrom && inv.alignerRangeTo
    ? `${inv.alignerRangeFrom}–${inv.alignerRangeTo}`
    : '—';
  text(rangeLabel, margin + 112 + 15, y + 10, { size: 9, align: 'center' });

  text(fmt(inv.rawSubtotal), W - margin - 3, y + 10, { size: 9, bold: true, align: 'right' });

  line(y + 18, [229, 231, 235]);
  y += 26;

  // ── Totals ──
  const totalsX      = W - margin - 60;
  const totalsLabelX = totalsX + 2;
  const totalsValX   = W - margin - 2;

  const discountAmount = parseFloat(inv.discountAmount ?? 0);
  const taxAmount      = parseFloat(inv.taxAmount ?? 0);

  const totalRows = [
    { label: 'Sous-total', val: fmt(inv.rawSubtotal), color: gray700 },
    ...(discountAmount > 0
      ? [{ label: 'Remise', val: `-${fmt(discountAmount)}`, color: green }]
      : []),
    ...(taxAmount > 0
      ? [{ label: `TVA (${inv.taxPercentage ?? 0}%)`, val: fmt(taxAmount), color: gray700 }]
      : []),
  ];

  totalRows.forEach((row) => {
    text(row.label, totalsLabelX, y + 5, { size: 8, color: gray400 });
    text(row.val,   totalsValX,   y + 5, { size: 8, bold: true, color: row.color, align: 'right' });
    line(y + 7);
    y += 9;
  });

  rect(totalsX, y, 60, 12, indigo);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...white);
  doc.text('TOTAL', totalsLabelX + 2, y + 8);
  doc.text(fmt(inv.rawTotal), totalsValX - 2, y + 8, { align: 'right' });
  y += 17;

  text('Montant payé', totalsLabelX, y + 5, { size: 8.5, color: gray400 });
  text(fmt(inv.rawAmountPaid), totalsValX, y + 5, { size: 8.5, bold: true, color: green, align: 'right' });
  line(y + 7);
  y += 9;

  const balanceDueNum  = parseFloat(inv.balanceDueRaw ?? inv.balanceDue ?? 0);
  const balanceColor   = balanceDueNum > 0 ? red : green;
  text('Solde dû',      totalsLabelX, y + 5, { size: 8.5, bold: true, color: gray700 });
  text(fmt(balanceDueNum), totalsValX, y + 5, { size: 8, bold: true, color: balanceColor, align: 'right' });
  y += 14;

  // ── Payment method ──
  if (inv.paymentMethod && inv.paymentMethod !== '—') {
    rect(margin, y, contentW, 10, gray100);
    text('Méthode de paiement :', margin + 4, y + 7, { size: 8, color: gray400 });
    text(inv.paymentMethod, margin + 50, y + 7, { size: 8, bold: true });
    y += 15;
  }

  // ── Notes ──
  if (inv.notes && inv.notes !== '—') {
    text('Notes :', margin, y + 5, { size: 8, bold: true, color: gray400 });
    y += 8;
    const noteLines = doc.splitTextToSize(inv.notes, contentW);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray700);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 5 + 6;
  }

  // ── Footer band ──
  const footerY = 285;
  rect(0, footerY, W, 12, indigo);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text(`Générée le ${new Date().toLocaleDateString('fr-FR')}`, margin, footerY + 7);
  doc.text(inv.number, W / 2, footerY + 7, { align: 'center' });
  doc.text('ClearMotion', W - margin, footerY + 7, { align: 'right' });

  doc.save(`Facture_${inv.number}.pdf`);
};

// ─────────────────────────────────────────────────────────────
// Micro-components
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = getStatus(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.cls}`}>
      <Icon name={cfg.icon} size={11} />
      {cfg.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, cls = 'text-foreground', bg = 'bg-muted/40', border = 'border-border' }) => (
  <div className={`${bg} border ${border} rounded-xl p-4`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon name={icon} size={14} className="text-muted-foreground" />
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
    <p className={`text-lg font-bold ${cls}`}>{value}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Expandable invoice row
// ─────────────────────────────────────────────────────────────
const InvoiceRow = ({ inv }) => {
  const [open, setOpen] = useState(false);
  const cfg = getStatus(inv.status);
  const isOverdue = inv.status === 'overdue';

  return (
    <div className="border border-border rounded-xl overflow-hidden transition-shadow hover:shadow-md">
      {/* Summary row */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setOpen((p) => !p)}
      >
        {/* Icon */}
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.cls}`}>
          <Icon name={cfg.icon} size={16} />
        </div>

        {/* Number + lot */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-foreground">{inv.number}</p>
            <StatusBadge status={inv.status} />
            {inv.phaseNumber && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                <Icon name="Layers" size={10} />
                Phase {inv.phaseNumber}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Lot : <span className="font-medium text-foreground">{inv.lotNumber}</span>
            {inv.phaseName && <> · <span className="font-medium text-foreground">{inv.phaseName}</span></>}
            {inv.issueDate !== '—' && <> · Émis le {inv.issueDate}</>}
          </p>
        </div>

        {/* Amount */}
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-primary">{inv.total}</p>
          {inv.balanceDue !== '—' && inv.balanceDue !== inv.total && (
            <p className={`text-xs ${isOverdue ? 'text-error font-semibold' : 'text-muted-foreground'}`}>
              Solde : {inv.balanceDue}
            </p>
          )}
        </div>

        <Icon
          name={open ? 'ChevronUp' : 'ChevronDown'}
          size={16}
          className="text-muted-foreground flex-shrink-0"
        />
      </div>

      {/* Expanded detail */}
      {open && (
        <div className="border-t border-border bg-muted/10 p-4 space-y-4">

          {/* Dates grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Date d'émission",  val: inv.issueDate },
              { label: "Date d'échéance",  val: inv.dueDate,      highlight: isOverdue },
              { label: 'Date de paiement', val: inv.paymentDate },
              { label: 'Méthode',          val: inv.paymentMethod },
            ].map(({ label, val, highlight }) => (
              <div key={label}>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className={`text-sm font-medium ${highlight ? 'text-error' : 'text-foreground'}`}>{val}</p>
              </div>
            ))}
          </div>

          {inv.lineItems?.length > 0 && (
            <div className="bg-background border border-border rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Détail des lignes
              </p>
              <ul className="space-y-1.5">
                {inv.lineItems.map((line, idx) => (
                  <li
                    key={`${line.description}-${idx}`}
                    className="flex justify-between gap-3 text-sm"
                  >
                    <span className="text-foreground">
                      {line.description}
                      {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                    </span>
                    <span className="font-medium text-foreground shrink-0">
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: inv.currencyCode || 'EUR',
                      }).format(line.lineTotal)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Financial breakdown */}
          <div className="bg-background border border-border rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Total TTC',     val: inv.total,      cls: 'text-primary font-bold' },
                { label: 'Montant payé',  val: inv.amountPaid, cls: 'text-success' },
                { label: 'Solde restant', val: inv.balanceDue, cls: isOverdue ? 'text-error font-semibold' : 'text-foreground' },
              ].map(({ label, val, cls }) => (
                <div key={label}>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-sm ${cls}`}>{val}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          {inv.notes && (
            <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg">
              <Icon name="MessageSquare" size={14} className="text-muted-foreground mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground">{inv.notes}</p>
            </div>
          )}

          {/* Actions — PDF download only */}
          <div className="flex items-center gap-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={() => downloadInvoicePdf(inv)}
            >
              Télécharger PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CaseInvoicesTab
// ─────────────────────────────────────────────────────────────
const CaseInvoicesTab = ({ caseId }) => {
  const { invoices, loading, error, refetch } = useCaseInvoices(caseId);

  if (!caseId) {
    return (
      <div className="text-center py-12 text-sm text-muted-foreground">
        Conception introuvable.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-20 bg-muted rounded-xl" />)}
        </div>
        {[1, 2, 3].map((i) => <div key={i} className="h-16 bg-muted rounded-xl" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Icon name="AlertTriangle" size={40} color="var(--color-error)" className="mb-3" />
        <p className="text-base font-semibold text-foreground mb-1">Erreur de chargement</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={refetch}>
          Réessayer
        </Button>
      </div>
    );
  }

  const totalCount   = invoices.length;
  const paidCount    = invoices.filter((i) => i.status === 'paid').length;
  const overdueCount = invoices.filter((i) => i.status === 'overdue').length;
  const pendingCount = invoices.filter((i) => ['pending', 'partial', 'partially_paid'].includes(i.status)).length;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Facturation</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Factures émises pour les lots de production de cette conception.
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" onClick={refetch}>
          Actualiser
        </Button>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-10 text-center space-y-2">
          <Icon name="Receipt" size={36} color="var(--color-muted-foreground)" className="mx-auto" />
          <p className="text-sm font-medium text-foreground">Aucune facture</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Aucune facture n'est associée à un lot de production pour cette conception.
            Les factures liées à un lot apparaîtront ici automatiquement.
          </p>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard icon="Receipt"     label="Total factures" value={totalCount}   cls="text-foreground" />
            <StatCard icon="CheckCircle" label="Payées"         value={paidCount}    cls="text-success"    bg="bg-success/5"    border="border-success/20" />
            <StatCard icon="Clock"       label="En attente"     value={pendingCount} cls="text-warning"    bg="bg-warning/5"    border="border-warning/20" />
            <StatCard icon="AlertCircle" label="En retard"      value={overdueCount} cls={overdueCount > 0 ? 'text-error' : 'text-muted-foreground'} bg={overdueCount > 0 ? 'bg-error/5' : 'bg-muted/40'} border={overdueCount > 0 ? 'border-error/20' : 'border-border'} />
          </div>

          {/* Overdue alert banner */}
          {overdueCount > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-error/10 border border-error/20 rounded-xl">
              <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
              <p className="text-sm text-error font-medium">
                {overdueCount} facture{overdueCount > 1 ? 's' : ''} en retard de paiement.
              </p>
            </div>
          )}

          {/* Invoice list */}
          <div className="space-y-3">
            {invoices.map((inv) => (
              <InvoiceRow key={inv.id} inv={inv} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CaseInvoicesTab;